import { TierNameValue } from "@ugg/shared/utils/tier-helpers";

// ─────────────────────────────────────────────────────────────────────
// Per-tier styling for the stacked tier-group cards used by the
// custom-mode tier lists (ARAM Mayhem champions + augments, League
// Classic champions).
//
// These lived in utils/tier-helpers.ts under an "ARAM_MAYHEM_" prefix,
// which was wrong twice over: they're presentation, not data, and
// Classic imported the Mayhem-named constants verbatim. They now sit
// next to their only consumer, TierGroupCard.
//
// IMPORTANT: these are written out as complete literal class strings
// rather than interpolated, so Tailwind's content scanner picks them up
// at build time. Both the website and monoapp tailwind configs glob
// `@ugg/shared/**/*.{ts,tsx}`, so this path is covered — but don't
// rewrite any of these into template strings.
// ─────────────────────────────────────────────────────────────────────

// Solid per-tier color. Used in three places on a card: the 1px
// horizontal divider between banner and body, and the small vertical
// accent bar left of the tier letter.
export const TIER_CARD_ACCENT_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#FF9B00]",
  S: "bg-[#3273FA]",
  A: "bg-[#7EA4F4]",
  B: "bg-[#CDDCFE]",
  C: "bg-[#FCB1B2]",
  D: "bg-[#FF4E50]",
};

// Border color (applied as the 4px left border on banner and body)
// combined with the gradient start color.
//
// The banner is `bg-gradient-to-r from-[#X]/<opacity> to-transparent`
// over a `bg-[#191937]` base — the tier color fades horizontally from
// the left, revealing the dark banner underneath. Opacity is 30% for
// every tier except B, where /20 is used because the much lighter
// #CDDCFE would otherwise overpower the gradient.
export const TIER_CARD_BANNER_CLASSES: Record<TierNameValue, string> = {
  "S+": "border-[#FF9B00] from-[#FF9B00]/30",
  S: "border-[#3273FA] from-[#3273FA]/30",
  A: "border-[#7EA4F4] from-[#7EA4F4]/30",
  B: "border-[#CDDCFE] from-[#CDDCFE]/20",
  C: "border-[#FCB1B2] from-[#FCB1B2]/30",
  D: "border-[#FF4E50] from-[#FF4E50]/30",
};

// Body background, alternating between the lighter (#191937) and darker
// (#11112A) navy to zebra-stripe the stacked cards. S+/A/C take the
// lighter shade — which also matches the banner background, so on those
// tiers the 1px divider is the only separator; S/B/D take the darker,
// so their banner reads visibly brighter than the body.
export const TIER_CARD_BODY_BG: Record<TierNameValue, string> = {
  "S+": "bg-[#191937]",
  S: "bg-[#11112A]",
  A: "bg-[#191937]",
  B: "bg-[#11112A]",
  C: "bg-[#191937]",
  D: "bg-[#11112A]",
};
